import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useAuth } from './use-auth'
import { authorizedFetch } from '@/lib/api'

export interface GroupInfo {
  name: string
  display_name: string
  is_assigned: boolean
  can_focus: boolean
}

export interface UserFocus {
  current_focus: string | null
  available_foci: string[]
  focus_confirmed: boolean
  role_switching_enabled?: boolean
}

export interface FocusStoreState {
  groups: GroupInfo[]
  focus: UserFocus | null
  isFetching: boolean
  selfServiceLocked: boolean
  roleSwitchingEnabled: boolean
  requestSeq: number
  fetchFocus: () => Promise<void>
  fetchGroups: () => Promise<void>
  updateGroups: (
    updates: Record<string, boolean>
  ) => Promise<{ current_focus: string | null; available_foci: string[]; focus_confirmed: boolean } | undefined>
  setFocus: (focus: string, threadId?: string | null) => Promise<any>
}

const FocusStoreContext = createContext<FocusStoreState | null>(null)

export function FocusStoreProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth()
  const [groups, setGroups] = useState<GroupInfo[]>([])
  const [focus, setFocusState] = useState<UserFocus | null>(null)
  const [selfServiceLocked, setSelfServiceLocked] = useState(false)
  const [roleSwitchingEnabled, setRoleSwitchingEnabled] = useState(true)
  const [isFetching] = useState(false)
  const seqRef = useRef(0)
  const initializedRef = useRef(false)
  const hasLoadedFocusRef = useRef(false)
  const hasLoadedGroupsRef = useRef(false)

  const seqRefs = useRef({
    groups: 0,
    focus: 0,
    update: 0,
    set: 0,
  })

  const controllersRef = useRef({
    groups: null as AbortController | null,
    focus: null as AbortController | null,
    update: null as AbortController | null,
    set: null as AbortController | null,
  })

  const fetchFocus = useCallback(async (): Promise<void> => {
    console.log('FocusStore: fetchFocus called')
    if (!token) {
      console.log('FocusStore: fetchFocus - no token, returning')
      return
    }

    if (controllersRef.current.focus) {
      controllersRef.current.focus.abort()
      console.log('FocusStore: Aborted in-flight focus request')
    }

    const controller = new AbortController()
    controllersRef.current.focus = controller

    const seq = ++seqRefs.current.focus
    console.log(`FocusStore: fetchFocus started (seq=${seq})`)

    try {
      const cacheBustParam = `t=${Date.now()}`
      const url = `/api/accounts/focus?${cacheBustParam}`

      const response = await authorizedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
        cache: 'no-store',
        signal: controller.signal,
      })

      console.log(`FocusStore: fetchFocus response (seq=${seq}, status=${response.status})`)

      if (!response.ok) {
        throw new Error('Failed to fetch focus')
      }

      const data = await response.json()
      console.log(`FocusStore: fetchFocus data (seq=${seq}):`, data)

      if (seq === seqRefs.current.focus) {
        console.log(`FocusStore: Applying fetchFocus result (seq=${seq})`)
        hasLoadedFocusRef.current = true
        setFocusState({
          ...data,
          role_switching_enabled: data.role_switching_enabled !== false,
        })
      } else {
        console.log(`FocusStore: Skipping stale fetchFocus result (seq=${seq}, current=${seqRefs.current.focus})`)
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log(`FocusStore: fetchFocus aborted (seq=${seq})`)
        return
      }
      console.error(`FocusStore: fetchFocus error (seq=${seq}):`, error)
      if (seq === seqRefs.current.focus && !hasLoadedFocusRef.current) {
        console.log(`FocusStore: Clearing focus due to error (seq=${seq}, no prior data)`)
        setFocusState(null)
      }
    }
  }, [token])

  const fetchGroups = useCallback(async (): Promise<void> => {
    console.log('FocusStore: fetchGroups called')
    if (!token) {
      console.log('FocusStore: fetchGroups - no token, returning')
      return
    }

    if (controllersRef.current.groups) {
      controllersRef.current.groups.abort()
      console.log('FocusStore: Aborted in-flight groups request')
    }

    const controller = new AbortController()
    controllersRef.current.groups = controller

    const seq = ++seqRefs.current.groups
    console.log(`FocusStore: fetchGroups started (seq=${seq})`)

    try {
      const response = await authorizedFetch('/api/accounts/groups', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
        cache: 'no-store',
        signal: controller.signal,
      })

      console.log(`FocusStore: fetchGroups response (seq=${seq}, status=${response.status})`)

      if (!response.ok) {
        throw new Error('Failed to fetch groups')
      }

      const data = await response.json()
      console.log(`FocusStore: fetchGroups data (seq=${seq}):`, data)

      if (seq === seqRefs.current.groups) {
        console.log(`FocusStore: Applying fetchGroups result (seq=${seq})`)
        hasLoadedGroupsRef.current = true
        setGroups(data.groups || [])
        setSelfServiceLocked(Boolean(data.self_service_locked))
        setRoleSwitchingEnabled(data.role_switching_enabled !== false)
      } else {
        console.log(`FocusStore: Skipping stale fetchGroups result (seq=${seq}, current=${seqRefs.current.groups})`)
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log(`FocusStore: fetchGroups aborted (seq=${seq})`)
        return
      }
      console.error(`FocusStore: fetchGroups error (seq=${seq}):`, error)
      if (seq === seqRefs.current.groups && !hasLoadedGroupsRef.current) {
        console.log(`FocusStore: Clearing groups due to error (seq=${seq}, no prior data)`)
        setGroups([])
      }
    }
  }, [token])

  const updateGroups = useCallback(
    async (
      updates: Record<string, boolean>
    ): Promise<
      { current_focus: string | null; available_foci: string[]; focus_confirmed: boolean } | undefined
    > => {
      if (!token) return undefined

      if (controllersRef.current.update) {
        controllersRef.current.update.abort()
        console.log('FocusStore: Aborted in-flight update request')
      }

      const controller = new AbortController()
      controllersRef.current.update = controller

      const seq = ++seqRefs.current.update
      console.log(`FocusStore: updateGroups started (seq=${seq}, updates=${JSON.stringify(updates)})`)

      try {
        const response = await authorizedFetch('/api/accounts/groups/update', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ group_updates: updates }),
          signal: controller.signal,
        })

        console.log(`FocusStore: updateGroups response (seq=${seq}, status=${response.status})`)

        if (!response.ok) {
          const errorData = await response.json()
          console.error(`FocusStore: updateGroups error response (seq=${seq}):`, errorData)
          throw new Error(errorData.error || 'Failed to update groups')
        }

        const data = await response.json()
        console.log(`FocusStore: updateGroups data (seq=${seq}):`, data)

        if (seq === seqRefs.current.update) {
          console.log(`FocusStore: Applying updateGroups result (seq=${seq})`)
          setGroups(data.groups || [])
          setSelfServiceLocked(Boolean(data.self_service_locked))
          const nextState = {
            current_focus: data.current_focus,
            available_foci: data.available_foci || [],
            focus_confirmed: data.focus_confirmed,
            role_switching_enabled: data.role_switching_enabled !== false,
          }
          setFocusState(nextState)
          setRoleSwitchingEnabled(data.role_switching_enabled !== false)
          return nextState
        } else {
          console.log(`FocusStore: Skipping stale updateGroups result (seq=${seq}, current=${seqRefs.current.update})`)
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.log(`FocusStore: updateGroups aborted (seq=${seq})`)
          return undefined
        }
        console.error(`FocusStore: updateGroups error (seq=${seq}):`, error)
        throw error
      }
      return undefined
    },
    [token]
  )

  const setFocus = useCallback(async (focusValue: string, threadId?: string | null): Promise<any> => {
    if (!token) return null

    if (controllersRef.current.set) {
      controllersRef.current.set.abort()
      console.log('FocusStore: Aborted in-flight set request')
    }

    const controller = new AbortController()
    controllersRef.current.set = controller

    const seq = ++seqRefs.current.set
    console.log(`FocusStore: setFocus started (seq=${seq}, focusValue=${focusValue}, threadId=${threadId ?? 'none'})`)

    try {
      const body: { focus: string; thread_id?: string } = { focus: focusValue }
      if (threadId && String(threadId).trim()) {
        body.thread_id = String(threadId).trim()
      }
      const response = await authorizedFetch('/api/accounts/focus', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      })

      console.log(`FocusStore: setFocus response (seq=${seq}, status=${response.status})`)

      if (!response.ok) {
        let errorData: { error?: string } = {}
        try {
          errorData = await response.json()
        } catch {
          errorData = { error: `Failed to set focus (${response.status})` }
        }
        console.error(`FocusStore: setFocus error response (seq=${seq}):`, errorData)
        throw new Error(errorData.error || 'Failed to set focus')
      }

      const data = await response.json()
      console.log(`FocusStore: setFocus data (seq=${seq}):`, data)

      if (seq === seqRefs.current.set) {
        console.log(`FocusStore: Applying setFocus result (seq=${seq})`)
        setFocusState({
          current_focus: data.current_focus,
          available_foci: data.available_foci || [],
          focus_confirmed: data.focus_confirmed,
        })

        return data
      } else {
        console.log(`FocusStore: Skipping stale setFocus result (seq=${seq}, current=${seqRefs.current.set})`)
        return null
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log(`FocusStore: setFocus aborted (seq=${seq})`)
        return
      }
      console.error(`FocusStore: setFocus error (seq=${seq}):`, error)
      throw error
    }
  }, [token])

  useEffect(() => {
    console.log('🔵 FocusStoreProvider: useEffect running', {
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 20) + '...' : 'null',
      initializedRef: initializedRef.current,
      currentGroupsCount: groups.length,
    })

    if (token && !initializedRef.current) {
      console.log('🟢 FocusStoreProvider: Token available and not yet initialized, initializing data')
      initializedRef.current = true
      fetchGroups().catch((err) => console.error('❌ FocusStoreProvider: fetchGroups error:', err))
      fetchFocus().catch((err) => console.error('❌ FocusStoreProvider: fetchFocus error:', err))
    } else if (token && initializedRef.current) {
      console.log('🟡 FocusStoreProvider: Token available but already initialized, skipping')
    } else if (!token) {
      console.log('🔴 FocusStoreProvider: No token available yet — reset store')
      initializedRef.current = false
      hasLoadedFocusRef.current = false
      hasLoadedGroupsRef.current = false
      setGroups([])
      setFocusState(null)
      setSelfServiceLocked(false)
      setRoleSwitchingEnabled(true)
    }
  }, [token, fetchGroups, fetchFocus])

  const value = useMemo<FocusStoreState>(
    () => ({
      groups,
      focus,
      isFetching,
      selfServiceLocked,
      roleSwitchingEnabled,
      requestSeq: seqRef.current,
      fetchFocus,
      fetchGroups,
      updateGroups,
      setFocus,
    }),
    [groups, focus, isFetching, selfServiceLocked, roleSwitchingEnabled, fetchFocus, fetchGroups, updateGroups, setFocus]
  )

  return <FocusStoreContext.Provider value={value}>{children}</FocusStoreContext.Provider>
}

export function useFocusStore(): FocusStoreState {
  const ctx = useContext(FocusStoreContext)
  if (!ctx) {
    throw new Error('useFocusStore must be used within a FocusStoreProvider')
  }
  return ctx
}
